import React, { Fragment, type ChangeEvent, useEffect, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl-next';
import Checkbox from '@atlaskit/checkbox';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { FadeIn } from '@atlaskit/motion';
import { type DirectAccessCheckboxListProps, type ViralSettingsByDomain } from './types';
import { messages } from '../i18n';
import { useAnalytics } from '../../analytics';
import { ViralSettingsAnalytics } from './ViralSettingsAnalytics';

const CheckboxWithAnalytics = ({
	children,
	domain,
	value,
	container,
}: {
	children: ReactNode;
	domain: string;
	value: boolean;
	container: string;
}) => {
	const analytics = useAnalytics(ViralSettingsAnalytics);

	useEffect(() => {
		analytics.directAccessCheckboxRendered({ domain, container, value });
	}, [domain, container, value, analytics]);

	return <Fragment>{children}</Fragment>;
};

export const DirectAccessCheckboxList = (props: DirectAccessCheckboxListProps) => {
	const analytics = useAnalytics(ViralSettingsAnalytics);
	const { viralSettingsByDomain, checkboxTheme = 'DEFAULT', onChange } = props;

	const onChangeWrapper = (updatedViralSettingsByDomain: ViralSettingsByDomain, domain: string) => {
		analytics.directAccessCheckboxClicked({
			value: updatedViralSettingsByDomain[domain].isChecked,
			domain,
			container: checkboxTheme,
		});
		onChange(updatedViralSettingsByDomain);
	};

	return (
		<Fragment>
			{Object.keys(viralSettingsByDomain)
				.filter((domain) => viralSettingsByDomain[domain].desPromotionEligible)
				.map((domain) => {
					const value = !!viralSettingsByDomain?.[domain].isChecked;
					return (
						<FadeIn
							entranceDirection="top"
							key={`viral-settings-direct-access-checkbox-fade-in${domain}`}
						>
							{(animationProps) => (
								<CheckboxWithAnalytics domain={domain} value={value} container={checkboxTheme}>
									<Box
										paddingBlockStart={checkboxTheme === 'MODAL' ? 'space.150' : 'space.050'}
										{...animationProps}
									>
										<Checkbox
											testId={`testId-invite-people-viral-settings-direct-access-${domain}`}
											key={`viral-settings-direct-access-checkbox-${domain}`}
											isChecked={value}
											onChange={(e: ChangeEvent<HTMLInputElement>) => {
												onChangeWrapper(
													{
														...viralSettingsByDomain,
														[domain]: {
															...viralSettingsByDomain[domain],
															isChecked: e.target.checked,
														},
													},
													domain,
												);
											}}
											label={
												<>
													<Text color="color.text">
														<FormattedMessage
															{...messages.directAccessDescription}
															values={{
																domainWithStyling: <b>&lt;@{domain}&gt;</b>,
															}}
														/>
													</Text>
													{checkboxTheme === 'MODAL' && (
														<Text color="color.text.subtlest" size="small">
															<FormattedMessage {...messages.directAccessExtendedDescription} />
														</Text>
													)}
												</>
											}
										/>
									</Box>
								</CheckboxWithAnalytics>
							)}
						</FadeIn>
					);
				})}
		</Fragment>
	);
};

export default DirectAccessCheckboxList;
